'use client';

import PropTypes from "prop-types";
import { memo, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Divider, Drawer, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import MenuList from "./MenuList";
import LogoSection from "../LogoSection";
import MenuCard from "./MenuCard";
import { openDrawer } from "@/app/store/slices/menu";
import { drawerWidth } from "@/app/store/constant";
import Chip from "@/app/ui-component/extended/Chip";
import { useIntl } from "react-intl";
import { getBaseURL } from "@/app/global-functions/baseURLUtils-administrators";
import UtilisateurImageComponent from "../Header/ProfileSection/UtilisateurImageComponent";
import SubCard from "@/app/ui-component/cards/SubCard";
import { useDispatch, useSelector } from "@/app/store";

const Sidebar = ({ window }) => {
  const theme = useTheme();
  const matchUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { paramMemoire } = useSelector((state) => state.diversParametersRedux);
  const [alterarSenha, setAlterarSenha] = useState(1);
  const intl = useIntl();
  const [userImage, setUserImage] = useState("");
  const [userName, setUserName] = useState("");
  const [role, setRole] = useState("");
  const [url, setUrl] = useState(getBaseURL());
  const [userID, setUserID] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [university, setUniversity] = useState("");

  const [roleSistea, setroleSistea] = useState("");
  const [fotoFileName, setfotoFileName] = useState("");


  const borderColor =
    theme.palette.mode === 'dark' ? 'white' : "black";

    useEffect(() => {
      if (paramMemoire && paramMemoire.length > 0) {
        getUserIDFromUserContext();
      }
    }, [paramMemoire, userImage], userName, university); // Only depend on paramMemoire


  const getUserIDFromUserContext = async () => {
    try {
     //  console.log("paramMemoire: ", paramMemoire);
  
      paramMemoire.map((item) => {
      //   console.log("Processing item: ", item);
  
        setfotoFileName(item.photofilename || "");
        setroleSistea(item.role);
  
        // General data
        setUserName(`${item.firstname} ${item.lastname}`);
        setUniversity(item.university);
  
        if (Number(item.typeUtilisateur) > 10) {
          setUserImage(`${url}/users/images?tipoDisplay=${fotoFileName}&typeUtilisateur=${item.typeUtilisateur}`);
          setRole(intl.formatMessage({ id: "role_candidat" }));
        } else if (Number(item.typeUtilisateur) === 3) {
          setUserImage(
            fotoFileName
              ? encodeURI(`${url}/studentMatriculas/images?tipoDisplay=${fotoFileName}`)
              : encodeURI(`${url}/studentMatriculas/images?tipoDisplay=semfoto.png`)
          );
          setRole(intl.formatMessage({ id: "role_candidat" }));
        } else if (Number(item.typeUtilisateur) === 1) {
          setUserImage(
            `${url}/users/images?tipoDisplay=${encodeURIComponent(fotoFileName)}&typeUtilisateur=${item.typeUtilisateur}`
          );
          setRole(item.role);
        } else if (Number(item.typeUtilisateur) === 2) {
          setUserImage(
            encodeURI(`${url}/professeur/images?tipoDisplay=${fotoFileName}&typeUtilisateur=${item.typeUtilisateur}`)
          );
          setRole(item.role);
        }
  
        setAlterarSenha(item.alterarSenha);
        setUserID(item.id);
        setFirstname(item.firstname);
        setLastname(item.lastname);
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const logo = useMemo(() => (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      <Box sx={{ display: "flex", p: 2, mx: "auto" }}>
        <LogoSection />
      </Box>
    </Box>
  ), []);

  const drawer = useMemo(() => (
    <PerfectScrollbar
      component="div"
      style={{
        overflowY: 'scroll',
        height: "calc(100vh - 127px)",
        paddingLeft: "16px",
        paddingRight: "16px",
      }}
    >
      <SubCard>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <UtilisateurImageComponent userImage={userImage} />
          <Typography variant="h6" sx={{ fontWeight: 400, textAlign: 'center' }}>
            {userName || "No User Name"}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, textAlign: 'center' }}>
            {university || "No University"}
          </Typography>
        </Box>
      </SubCard>
      <Divider sx={{ borderColor: borderColor, borderStyle: "solid", marginY: 1 }} />
      {Number(alterarSenha) === 1 && (
        <div>
          <PerfectScrollbar>
            <MenuList />
            <MenuCard />
          </PerfectScrollbar>
        </div>
      )}
      <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
        <Chip style={{ color: "black" }}
          label={process.env.NEXT_PUBLIC_VERSION}
          disabled
          chipcolor="secondary"
          size="small"
          sx={{ cursor: "pointer" }}
        />
      </Stack>
    </PerfectScrollbar>
  ), [matchUpMd, borderColor, userImage], userName, university);

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : "auto" }}
      aria-label="mailbox folders"
    >
      <Drawer
        container={container}
        variant={matchUpMd ? "persistent" : "temporary"}
        anchor="left"
        open={drawerOpen}
        onClose={() => dispatch(openDrawer(!drawerOpen))}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: "none",
            [theme.breakpoints.up("md")]: {
              top: "88px",
            },
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        {drawerOpen && logo}
        {drawerOpen && drawer}
      </Drawer>
      
    </Box>
  );
};

Sidebar.propTypes = {
  window: PropTypes.object,
};

export default memo(Sidebar);